import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {kindLabelKey} from './contextPackConstants';

const CHANNELS = ['email', 'whatsapp'];

// A4 — "Pedile el material a tu cliente". Modal sin backend: arma un mensaje listo para copiar
// que la agencia le manda a SU cliente pidiéndole el material que todavía falta (solo los ítems
// de destino MEMORY). Dos tonos: email (profesional cercano) y WhatsApp (más corto e informal).
// El mensaje está en lenguaje de cliente final — sin jerga Dashbo/IA: "para conocerte mejor y
// trabajar con más contexto". Se interpola el nombre del cliente + la lista de faltantes.
export function ContextPackRequestModal({isOpen, onClose, clientName, missingItems}) {
    const {t} = useTranslation();
    const [channel, setChannel] = useState('email');
    const [copied, setCopied] = useState(false);

    // Lista de faltantes como bullets con el label de cada ítem (mismos labels del checklist).
    const itemsList = useMemo(() => (
        (missingItems || []).map((item) => `- ${t(kindLabelKey(item.kind))}`).join('\n')
    ), [missingItems, t]);

    const message = useMemo(() => {
        const name = (clientName || '').trim();
        // Saludo robusto: si no tenemos el nombre, "¡Hola!" en vez de un "Hola :" roto.
        const greeting = name
            ? t(`client_memory.context_pack.request_modal.greeting_${channel}`,
                {name, interpolation: {escapeValue: false}})
            : t('client_memory.context_pack.request_modal.greeting_generic');
        return t(`client_memory.context_pack.request_modal.body_${channel}`, {
            greeting,
            items: itemsList,
            interpolation: {escapeValue: false},
        });
    }, [t, channel, clientName, itemsList]);

    const handleCopy = () => {
        // El clipboard puede estar bloqueado (contexto no seguro / permiso denegado): avisar en vez
        // de fallar en silencio (el usuario igual puede seleccionar el texto del textarea a mano).
        if (!navigator.clipboard) {
            toast.error(t('client_memory.context_pack.request_modal.copy_error'));
            return;
        }
        navigator.clipboard.writeText(message).then(() => {
            setCopied(true);
            toast.success(t('client_memory.context_pack.request_modal.copied'));
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {
            toast.error(t('client_memory.context_pack.request_modal.copy_error'));
        });
    };

    const selectChannel = (c) => {
        setChannel(c);
        setCopied(false);
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} className="cmem-cpack-request-modal">
            <ModalHeader toggle={onClose}>
                {t('client_memory.context_pack.request_modal.title')}
            </ModalHeader>
            <ModalBody>
                <div className="cmem-cpack-request-body">
                    <p className="cmem-cpack-request-lead">
                        {t('client_memory.context_pack.request_modal.lead')}
                    </p>
                    <div className="cmem-cpack-tabs" role="group"
                         aria-label={t('client_memory.context_pack.request_modal.channel_label')}>
                        {CHANNELS.map((c) => (
                            <button
                                key={c}
                                type="button"
                                className={`cmem-cpack-tab ${channel === c ? '-active' : ''}`}
                                aria-pressed={channel === c}
                                onClick={() => selectChannel(c)}
                            >
                                {t(`client_memory.context_pack.request_modal.tab_${c}`)}
                            </button>
                        ))}
                    </div>
                    <textarea
                        className="form-control cmem-cpack-request-text"
                        readOnly
                        rows={channel === 'whatsapp' ? 8 : 12}
                        value={message}
                        aria-label={t('client_memory.context_pack.request_modal.title')}
                    />
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn -negative" onClick={onClose}>
                    {t('common.cancel')}
                </button>
                <button type="button" className="btn -alt" onClick={handleCopy}>
                    {copied
                        ? t('client_memory.context_pack.request_modal.copied')
                        : t('client_memory.context_pack.request_modal.copy')}
                </button>
            </ModalFooter>
        </Modal>
    );
}
